import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { v4 as uuid4 } from 'uuid';
import { AssetListSharedState, AssetListStateService } from './asset-list-state.service';
import { AuthService } from '../../../services/auth.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AssetModelComponent } from '../../asset-model/asset-model.component';
import { Asset, SubAsset } from '../models/asset.model';
import { CHECKBOX_EMPTY_ICON, CHECKBOX_FULL_ICON, CHECKBOX_PARTIAL_ICON } from '../constants/funnel-header.constants';
import { User } from '../../../interfaces';

@Injectable({
  providedIn: 'root',
})
export class FunnelHeaderService {
  constructor(
    private readonly stateService: AssetListStateService,
    private readonly authService: AuthService,
    private readonly modalService: NgbModal,
    private readonly translatePipe: TranslatePipe
  ) {}

  public getCheckboxImage(category: string, bundle: Asset[]): string {
    const assets: Asset[] = this.getGroupedAsset(category, bundle);
    const state: AssetListSharedState = this.stateService.currentState;
    const selectedCount: number = assets.filter((asset: Asset) => state.selectedAssetTypes.includes(asset.id)).length;
    if (selectedCount === assets.length) {
      return CHECKBOX_FULL_ICON;
    } else if (selectedCount === 0) {
      return CHECKBOX_EMPTY_ICON;
    } else {
      return CHECKBOX_PARTIAL_ICON;
    }
  }

  public isAllassetSelected(funnelType: string, bundle: Asset[], projectId?: string): boolean {
    const groupedAssets: Asset[] = this.getGroupedAsset(funnelType, bundle);
    const state: AssetListSharedState = this.stateService.currentState;

    const selectableAssets: Asset[] = groupedAssets.filter(
      (asset: Asset) => !state.affectedAssets[asset.id] || state.affectedAssets[asset.id]?.projectId === projectId
    );

    if (selectableAssets.length === 0) return false;

    return selectableAssets.every((asset: Asset) => state.selectedAssetTypes.includes(asset.id));
  }

  public isAllSelectedFromOther(funnelType: string, bundle: Asset[], projectId?: string): boolean {
    const groupedAssets: Asset[] = this.getGroupedAsset(funnelType, bundle);
    const state: AssetListSharedState = this.stateService.currentState;
    const selectableAssets: Asset[] = groupedAssets.filter(
      (asset: Asset) => !!state.affectedAssets[asset.id] && state.affectedAssets[asset.id]?.projectId !== projectId
    );

    return selectableAssets?.length === groupedAssets?.length;
  }

  public toggleSelectAll(
    category: string,
    bundle: Asset[],
    assetSelectedCallback?: (selectedAssetTypes: string[]) => void
  ): void {
    const assets: Asset[] = this.getGroupedAsset(category, bundle);
    const state: AssetListSharedState = this.stateService.currentState;
    let selectedAssetTypes: string[] = [...state.selectedAssetTypes];
    const allSelected: boolean = assets.every((asset: Asset) => selectedAssetTypes.includes(asset.id));
    if (allSelected) {
      selectedAssetTypes = selectedAssetTypes.filter((id: string) => !assets.some((asset: Asset) => asset.id === id));
    } else {
      for (const asset of assets) {
        if (!selectedAssetTypes.includes(asset.id)) {
          selectedAssetTypes.push(asset.id);
        }
      }
    }
    this.stateService.updateState({ selectedAssetTypes });
    if (assetSelectedCallback) {
      assetSelectedCallback(selectedAssetTypes);
    }
  }

  public selecteAllasset(category: string, bundle: Asset[], projectId?: string): void {
    let assets: Asset[] = this.getGroupedAsset(category, bundle);
    const state: AssetListSharedState = this.stateService.currentState;

    assets = assets.filter(
      (asset: Asset) =>
        (!!state.affectedAssets[asset.id] && state.affectedAssets[asset.id]?.projectId === projectId) ||
        !state.affectedAssets[asset.id]
    );

    const allSelected: boolean = assets.every((asset: Asset) => state.selectedAssetTypes.includes(asset.id));
    if (allSelected) {
      for (const asset of assets) {
        if (state.selectedAssetTypes.includes(asset.id)) {
          this.selecteAsset(asset.id);
        }
      }
    } else {
      for (const asset of assets) {
        if (!state.selectedAssetTypes.includes(asset.id)) {
          this.selecteAsset(asset.id);
        }
      }
    }
  }

  private selecteAsset(id: string): void {
    const state: AssetListSharedState = this.stateService.currentState;
    const selectedAssetTypes: string[] = [...state.selectedAssetTypes];
    const index: number = selectedAssetTypes.indexOf(id);
    if (index === -1) {
      selectedAssetTypes.push(id);
    } else {
      selectedAssetTypes.splice(index, 1);
    }

    const selectedAssetId: string[] = [...state.selectedAssetId];
    const indexInSelectedId: number = selectedAssetId.indexOf(id);
    if (indexInSelectedId === -1) {
      selectedAssetId.push(id);
    } else {
      selectedAssetId.splice(indexInSelectedId, 1);
    }
    this.stateService.updateState({ selectedAssetTypes, selectedAssetId });
  }

  public areAllSelectedHidden(category: string, bundle: Asset[]): boolean {
    const assets: Asset[] = this.getGroupedAsset(category, bundle);
    const state: AssetListSharedState = this.stateService.currentState;
    const selectedAssets: Asset[] = assets.filter((asset: Asset) => state.selectedAssetTypes.includes(asset.id));

    if (selectedAssets.length === 0) {
      return false;
    }

    return selectedAssets.every((asset: Asset) => asset.hidden === true);
  }

  public getGroupedAsset(funnel: string, bundle: Asset[]): Asset[] {
    const result: Asset[] = [];
    const state: AssetListSharedState = this.stateService.currentState;
    const targetFunnel: string = funnel.toUpperCase();
    let isFirstSet: boolean = false;

    let { first_conversion_asset } = state;
    let { first_consideration_asset } = state;
    let { first_awarness_asset } = state;

    if (!bundle?.length || !funnel) {
      this.persistFirstAssetState(first_conversion_asset, first_consideration_asset, first_awarness_asset);
      return result;
    }

    for (const item of bundle) {
      const itemFunnel: string | undefined = item.funnel?.toUpperCase();
      if (!itemFunnel || itemFunnel !== targetFunnel) {
        continue;
      }

      result.push(item);

      if (isFirstSet || item.hidden) {
        continue;
      }

      const firstAsset: { conversion?: string; consideration?: string; awarness?: string } = this.setFirstAsset(
        itemFunnel,
        state.funnelList,
        item.id
      );

      const assigned: boolean = this.applyFirstAsset(firstAsset, {
        conversion: (value: string) => {
          first_conversion_asset = value;
        },
        consideration: (value: string) => {
          first_consideration_asset = value;
        },
        awarness: (value: string) => {
          first_awarness_asset = value;
        },
      });

      if (assigned) {
        isFirstSet = true;
      }
    }

    this.persistFirstAssetState(first_conversion_asset, first_consideration_asset, first_awarness_asset);

    return result;
  }

  private setFirstAsset(
    itemFunnel: string,
    funnelList: string[],
    itemId: string
  ): { conversion?: string; consideration?: string; awarness?: string } {
    if (itemFunnel === funnelList[2]) {
      return { conversion: itemId };
    } else if (itemFunnel === funnelList[1]) {
      return { consideration: itemId };
    } else if (itemFunnel === funnelList[0]) {
      return { awarness: itemId };
    }
    return {};
  }

  private applyFirstAsset(
    firstAsset: { conversion?: string; consideration?: string; awarness?: string },
    setters: {
      conversion: (value: string) => void;
      consideration: (value: string) => void;
      awarness: (value: string) => void;
    }
  ): boolean {
    type FirstAssetKey = 'conversion' | 'consideration' | 'awarness';
    let applied: boolean = false;

    for (const key of ['conversion', 'consideration', 'awarness'] as FirstAssetKey[]) {
      const value: string | undefined = firstAsset[key];
      if (value) {
        setters[key](value);
        applied = true;
      }
    }

    return applied;
  }

  private persistFirstAssetState(
    conversion: string | undefined,
    consideration: string | undefined,
    awarness: string | undefined
  ): void {
    this.stateService.updateState({
      first_conversion_asset: conversion,
      first_consideration_asset: consideration,
      first_awarness_asset: awarness,
    });
  }

  public hasSelectedAssets(category: string, bundle: Asset[]): boolean {
    const assets: Asset[] = this.getGroupedAsset(category, bundle);
    const state: AssetListSharedState = this.stateService.currentState;
    return assets.some((asset: Asset) => state.selectedAssetTypes.includes(asset.id));
  }

  public toggleHideSelectedAssets(
    category: string,
    bundle: Asset[],
    originalBundle: Asset[],
    bulkUpdateCallback?: (data: { action: string; assetIds: string[] }) => void,
    bundleChangeCallback?: (bundle: Asset[]) => void
  ): { newBundle: Asset[]; newOriginalBundle: Asset[] } {
    const assets: Asset[] = this.getGroupedAsset(category, bundle);
    const state: AssetListSharedState = this.stateService.currentState;
    const selectedAssets: Asset[] = assets.filter((asset: Asset) => state.selectedAssetTypes.includes(asset.id));
    const allHidden: boolean = selectedAssets.every((asset: Asset) => asset.hidden === true);

    const assetIdsToUpdate: string[] = selectedAssets.map((asset: Asset) => asset.id);
    const action: string = allHidden ? 'UNHIDE' : 'HIDE';

    const newOriginalBundle: Asset[] = originalBundle.map((a: Asset) => {
      if (assetIdsToUpdate.includes(a.id)) {
        return { ...a, hidden: !allHidden };
      }
      return a;
    });

    let allBundle: Asset[] = [...state.allBundle];
    if (allBundle.length) {
      allBundle = allBundle.map((a: Asset) => {
        if (assetIdsToUpdate.includes(a.id)) {
          return { ...a, hidden: !allHidden };
        }
        return a;
      });
    }

    const newBundle: Asset[] = bundle.map((a: Asset) => {
      if (assetIdsToUpdate.includes(a.id)) {
        return { ...a, hidden: !allHidden };
      }
      return a;
    });

    this.stateService.updateState({
      allBundle,
      allAssets: structuredClone(newOriginalBundle),
    });

    if (bulkUpdateCallback) {
      bulkUpdateCallback({
        action: action,
        assetIds: assetIdsToUpdate,
      });
    }

    if (bundleChangeCallback) {
      bundleChangeCallback(newBundle);
    }

    return { newBundle, newOriginalBundle };
  }

  public canDeleteAllSelected(groupName: string, bundle: Asset[]): boolean {
    const allGroupAssets: Asset[] = this.getGroupedAsset(groupName, bundle);
    const state: AssetListSharedState = this.stateService.currentState;
    const selectedAssets: Asset[] = allGroupAssets.filter((asset: Asset) =>
      state.selectedAssetTypes.includes(asset.id)
    );

    if (selectedAssets.length === 0) return false;

    const hasAffectedAssets: boolean = selectedAssets.some((asset: Asset) => state.affectedAssets[asset.id]);

    if (hasAffectedAssets) return false;

    return selectedAssets.every((asset: Asset) => asset.initial === false && this.isCreator(asset.createdBy));
  }

  private isCreator(createdBy: string): boolean {
    const authUser: User = this.authService.getAuthUser();
    return authUser?.email === createdBy;
  }

  public deleteSelectedAssets(
    groupName: string,
    bundle: Asset[],
    originalBundle: Asset[],
    bulkUpdateCallback?: (data: { action: string; assetIds: string[] }) => void,
    bundleChangeCallback?: (bundle: Asset[]) => void
  ): Promise<{ newBundle: Asset[]; newOriginalBundle: Asset[] } | null> {
    const allGroupAssets: Asset[] = this.getGroupedAsset(groupName, bundle);
    const state: AssetListSharedState = this.stateService.currentState;
    const selectedAssets: Asset[] = allGroupAssets.filter((asset: Asset) =>
      state.selectedAssetTypes.includes(asset.id)
    );

    if (selectedAssets.length === 0) return null;

    const modalRef: NgbModalRef = this.modalService.open(AssetModelComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    const assetCount: number = selectedAssets.length;
    modalRef.componentInstance.title = this.translatePipe.transform('asset_deleteAsset') || 'Delete Asset(s)';
    modalRef.componentInstance.description = `Are you sure you want to delete these <strong>"${assetCount}"</strong> selected assets?`;

    return modalRef.result
      .then((confirm: boolean) => {
        if (!confirm) return null;

        const idsToDelete: string[] = selectedAssets.map((asset: Asset) => asset.id);

        const newOriginalBundle: Asset[] = originalBundle.filter((asset: Asset) => !idsToDelete.includes(asset.id));
        const allBundle: Asset[] = state.allBundle.filter((asset: Asset) => !idsToDelete.includes(asset.id));
        const newBundle: Asset[] = bundle.filter((asset: Asset) => !idsToDelete.includes(asset.id));

        const selectedAssetTypes: string[] = state.selectedAssetTypes.filter((id: string) => !idsToDelete.includes(id));

        this.stateService.updateState({
          allBundle,
          allAssets: structuredClone(newOriginalBundle),
          selectedAssetTypes,
        });

        if (bulkUpdateCallback) {
          bulkUpdateCallback({
            action: 'DELETE',
            assetIds: idsToDelete,
          });
        }

        if (bundleChangeCallback) {
          bundleChangeCallback(newBundle);
        }

        return { newBundle, newOriginalBundle };
      })
      .catch(() => null);
  }

  public duplicateSelectedAssets(
    groupName: string,
    bundle: Asset[],
    originalBundle: Asset[],
    assetStar: string,
    projectId?: string,
    asWidget?: boolean,
    bulkUpdateCallback?: (data: { action: string; assetIds: string[] }) => void,
    bundleChangeCallback?: (bundle: Asset[]) => void,
    timelineUpdateCallback?: (data: {
      allBundle: Asset[];
      newAssets: Asset[];
      insertAfterMap: { newAssetId: string; afterId: string }[];
    }) => void
  ): { newBundle: Asset[]; newOriginalBundle: Asset[] } | null {
    const allGroupAssets: Asset[] = this.getGroupedAsset(groupName, bundle);
    const state: AssetListSharedState = this.stateService.currentState;
    const selectedAssets: Asset[] = allGroupAssets.filter((asset: Asset) =>
      state.selectedAssetTypes.includes(asset.id)
    );

    if (selectedAssets.length === 0) return null;

    const newOriginalBundle: Asset[] = [...originalBundle];
    const newAllBundle: Asset[] = [...state.allBundle];
    const newBundle: Asset[] = [...bundle];

    const duplicates: Asset[] = [];
    const originalIds: string[] = [];

    for (const originalAsset of selectedAssets) {
      originalIds.push(originalAsset.id);

      const duplicate: Asset = {
        id: uuid4(),
        assetBundle: assetStar,
        funnel: originalAsset.funnel,
        focus: originalAsset.focus,
        type: originalAsset.type,
        detail: `Copy of ${originalAsset.detail || originalAsset.title}`,
        title: `Copy of ${originalAsset.title || originalAsset.detail}`,
        bets: [...(originalAsset.bets || [])],
        categories: [...(originalAsset.categories || [])],
        companyId: originalAsset.companyId,
        format: [...(originalAsset.format || [])],
        length: [...(originalAsset.length || [])],
        subAsset: (originalAsset.subAsset || []).map((subAsset: SubAsset) => {
          const { ...subAssetWithoutId } = subAsset;
          return subAssetWithoutId;
        }),
        createdBy: this.authService.getAuthUser().email,
        initial: false,
        assetType: 'MASTER',
        sortOrder: Date.now() + duplicates.length,
      };

      duplicates.push(duplicate);
    }

    let insertedCount: number = 0;

    for (const [index, asset] of originalBundle.entries()) {
      if (originalIds.includes(asset.id)) {
        const duplicateIndex: number = originalIds.indexOf(asset.id);
        const duplicate: Asset = duplicates[duplicateIndex];

        newOriginalBundle.splice(index + insertedCount, 0, duplicate);

        const allBundleIndex: number = state.allBundle.findIndex((item: Asset) => item.id === asset.id);
        if (allBundleIndex !== -1) {
          newAllBundle.splice(allBundleIndex + insertedCount, 0, duplicate);
        }

        const bundleIndex: number = bundle.findIndex((item: Asset) => item.id === asset.id);
        if (bundleIndex !== -1) {
          newBundle.splice(bundleIndex + insertedCount, 0, duplicate);
        }

        insertedCount++;
      }
    }

    this.stateService.updateState({
      allBundle: newAllBundle,
      allAssets: structuredClone(newOriginalBundle),
      selectedAssetTypes: [],
    });

    if (bulkUpdateCallback) {
      bulkUpdateCallback({
        action: 'DUPLICATE',
        assetIds: originalIds,
      });
    }

    if (bundleChangeCallback) {
      bundleChangeCallback(newBundle);
    }

    if (projectId && asWidget && timelineUpdateCallback) {
      timelineUpdateCallback({
        allBundle: state.allBundle,
        newAssets: duplicates,
        insertAfterMap: duplicates.map((dup: Asset, i: number) => ({
          newAssetId: dup.id,
          afterId: originalIds[i],
        })),
      });
    }
    return { newBundle, newOriginalBundle };
  }
}
